import { Observable } from "rxjs";
import _ from "lodash";

import { ProductScheduleSlotsRepository } from "lib/repositories/index";
import { ProductScheduleSlotDecorator } from "lib/decorators/index";
import st from "lib/utils/index";
import { createAlert, removeAlert } from "modules/app/actions";

export const stateSelector = (state) => state.products.EditProductView.StepAvailability;

export const INITIALIZE_VIEW = "products/EditProductView/StepAvailability/INITIALIZE_VIEW";
export const FETCH_SLOTS = "products/EditProductView/FETCH_SLOTS";
export const FETCH_SLOTS_START = "products/EditProductView/FETCH_SLOTS_START";
export const FETCH_SLOTS_SUCCESS = "products/EditProductView/FETCH_SLOTS_SUCCESS";
export const FETCH_SLOTS_FAILURE = "products/EditProductView/FETCH_SLOTS_FAILURE";
export const UPDATE_SLOT = "products/EditProductView/StepAvailability/UPDATE_SLOT";
export const OPEN_SLOT_FORM = "products/EditProductView/StepAvailability/OPEN_SLOT_FORM";
export const CLOSE_FORM = "products/EditProductView/StepAvailability/CLOSE_FORM";
export const VALIDATE = "products/EditProductView/StepAvailability/VALIDATE";
export const SET_VALIDATION_ERRORS = "products/EditProductView/StepAvailability/SET_VALIDATION_ERRORS";
export const VALIDATE_SUCCESS = "products/EditProductView/StepAvailability/VALIDATE_SUCCESS";
export const VALIDATE_FAILURE = "products/EditProductView/StepAvailability/VALIDATE_FAILURE";
export const SAVE = "products/EditProductView/StepAvailability/SAVE";
export const FETCH_SAVE_START = "products/EditProductView/StepAvailability/FETCH_SAVE_START";
export const FETCH_SAVE_SUCCESS = "products/EditProductView/StepAvailability/FETCH_SAVE_SUCCESS";
export const FETCH_SAVE_FAILURE = "products/EditProductView/StepAvailability/FETCH_SAVE_FAILURE";
export const REMOVE_SLOT = "products/EditProductView/StepAvailability/REMOVE_SLOT";
export const FETCH_REMOVE_SLOT_START = "products/EditProductView/StepAvailability/FETCH_REMOVE_SLOT_START";
export const FETCH_REMOVE_SLOT_SUCCESS = "products/EditProductView/StepAvailability/FETCH_REMOVE_SLOT_SUCCESS";
export const FETCH_REMOVE_SLOT_FAILURE = "products/EditProductView/StepAvailability/FETCH_REMOVE_SLOT_FAILURE";
export const SET_IS_SCHEDULE_FORM_OPENED = "products/EditProductView/StepAvailability/SET_IS_SCHEDULE_FORM_OPENED";
export const SET_DATE_RANGE = "products/EditProductView/StepAvailability/SET_DATE_RANGE";

const initialState = {
  product: null,
  slots: [],
  dateRange: null,
  initialEditedSlot: null,
  editedSlot: null,
  isScheduleBeingEdited: false,
  errors: [],
  valid: true,
  fetchDone: false,
  fetchLoading: false,
  fetchFailed: false,
  submitLoading: false,
};

const reducers = {
  [INITIALIZE_VIEW]: (state, { product }) => {
    return {
      ...initialState,
      product,
      dateRange: {
        starts_at_gte: moment().startOf("month").add(-7, "days").format(moment.RUBY_DATE_FORMAT),
        starts_at_lte: moment().endOf("month").add(7, "days").format(moment.RUBY_DATE_FORMAT),
      },
    };
  },

  [FETCH_SLOTS_START]: (state) => {
    return {
      ...state,
      fetchLoading: true,
      fetchFailed: false,
    };
  },

  [FETCH_SLOTS_SUCCESS]: (state, { slots }) => {
    return {
      ...state,
      slots,
      fetchDone: true,
      fetchLoading: false,
      fetchFailed: false,
    };
  },

  [FETCH_SLOTS_FAILURE]: (state, { error }) => {
    return {
      ...state,
      fetchLoading: false,
      fetchFailed: true,
    };
  },

  [OPEN_SLOT_FORM]: (state, { slot: editedSlot }) => {
    const initialEditedSlot = state.slots.find((sl) => sl.id === editedSlot.id);
    return {
      ...state,
      initialEditedSlot,
      editedSlot,
      isScheduleBeingEdited: !!editedSlot._forceScheduleEdit || false,
    };
  },

  [CLOSE_FORM]: (state) => {
    return {
      ...state,
      initialEditedSlot: null,
      editedSlot: null,
    };
  },

  [UPDATE_SLOT]: (state, { slot: editedSlot }) => {
    const product_variant =
      state.product.product_variants.find((pv) => pv.id === editedSlot.product_variant_id) ||
      state.product.product_variants[0];

    editedSlot = new ProductScheduleSlotDecorator(editedSlot);
    editedSlot.calculatePricings({ product_variant });
    if (state.isScheduleBeingEdited && editedSlot.product_schedule) {
      editedSlot.normalizeSchedule();
    }
    return {
      ...state,
      editedSlot,
    };
  },

  [SET_VALIDATION_ERRORS]: (state, { errors }) => {
    return {
      ...state,
      errors,
      valid: errors.length === 0,
    };
  },

  [FETCH_SAVE_START]: (state) => {
    return {
      ...state,
      submitLoading: true,
    };
  },

  [FETCH_SAVE_SUCCESS]: (state, { slot }) => {
    const slots = _([slot]).concat(state.slots).uniqBy("id").value();
    return {
      ...state,
      slots,
      initialEditedSlot: slot,
      editedSlot: slot,
      valid: true,
      submitLoading: false,
    };
  },

  [FETCH_SAVE_FAILURE]: (state, { error }) => {
    return {
      ...state,
      submitLoading: false,
    };
  },

  [FETCH_REMOVE_SLOT_START]: (state) => {
    return {
      ...state,
      submitLoading: true,
    };
  },

  [FETCH_REMOVE_SLOT_SUCCESS]: (state, { slot }) => {
    const slots = state.slots.filter((sl) => sl.id !== slot.id);
    return {
      ...state,
      slots,
      submitLoading: false,
    };
  },

  [FETCH_REMOVE_SLOT_FAILURE]: (state, { error }) => {
    return {
      ...state,
      submitLoading: false,
    };
  },

  [SET_DATE_RANGE]: (state, { dateRange }) => {
    return {
      ...state,
      dateRange,
    };
  },

  [SET_IS_SCHEDULE_FORM_OPENED]: (state, { isScheduleBeingEdited }) => {
    let { editedSlot } = state;
    if (isScheduleBeingEdited) {
      editedSlot = new ProductScheduleSlotDecorator(editedSlot);
      editedSlot.normalizeSchedule();
    } else {
      editedSlot = new ProductScheduleSlotDecorator(editedSlot);
      editedSlot.product_schedule = state.initialEditedSlot ? state.initialEditedSlot.product_schedule : undefined;
    }
    return {
      ...state,
      editedSlot,
      isScheduleBeingEdited,
    };
  },
};

const epics = [
  (actions$) => actions$.ofType(INITIALIZE_VIEW).mapTo({ type: FETCH_SLOTS }),

  (actions$, { getState, dispatch }) =>
    actions$.ofType(FETCH_SLOTS).switchMap(() => {
      const { product, dateRange } = stateSelector(getState());
      return Observable.concat(
        Observable.of({ type: FETCH_SLOTS_START }),
        ProductScheduleSlotsRepository.findAll({
          product_id: product.id,
          starts_at_gte: dateRange ? dateRange.starts_at_gte : undefined,
          starts_at_lte: dateRange ? dateRange.starts_at_lte : undefined,
        })
          .map((slots) => {
            return { type: FETCH_SLOTS_SUCCESS, slots };
          })
          .catch((error) => {
            st.api.handleError(error, { getState, dispatch });
            return Observable.of({ type: FETCH_SLOTS_FAILURE, error });
          })
      );
    }),

  (actions$) =>
    actions$.ofType(SET_DATE_RANGE).debounceTime(300).mapTo({ type: FETCH_SLOTS }),

  (actions$, { getState }) =>
    actions$.ofType(VALIDATE).switchMap(({ submitValidate }) => {
      const { editedSlot } = stateSelector(getState());
      const errors = new st.form.ValidationErrors();

      submitValidate && errors.add(`editedSlot.start_date`, st.validators.presence(editedSlot.start_date));
      submitValidate && errors.add(`editedSlot.end_date`, st.validators.presence(editedSlot.end_date));

      if (editedSlot.start_time || editedSlot.end_time) {
        submitValidate && errors.add(`editedSlot.start_time`, st.validators.presence(editedSlot.start_time));
        submitValidate && errors.add(`editedSlot.end_time`, st.validators.presence(editedSlot.end_time));
      }

      submitValidate &&
        editedSlot.starts_at &&
        editedSlot.ends_at &&
        errors.add(
          `editedSlot.end_time`,
          st.validators.range(new Date(editedSlot.ends_at), {
            greater_than_or_equal_to: new Date(editedSlot.starts_at),
          })
        );

      if (errors.length > 0) {
        errors.add("base", I18n.t("papp.common.form_validation_error_message"));
      }

      return Observable.concat(
        Observable.of({ type: SET_VALIDATION_ERRORS, errors }),
        Observable.of({ type: errors.length > 0 ? VALIDATE_FAILURE : VALIDATE_SUCCESS })
      );
    }),

  // SAVE
  (actions$, { dispatch, getState }) =>
    actions$
      .ofType(SAVE)
      .concatMap(({ withAllFutureSlots }) => {
        setTimeout(() => {
          dispatch(validate({ submitValidate: true }));
        });
        return Observable.race(actions$.ofType(VALIDATE_FAILURE), actions$.ofType(VALIDATE_SUCCESS))
          .take(1)
          .map(({ type }) => ({ type, withAllFutureSlots }));
      })
      .filter(({ type }) => type === VALIDATE_SUCCESS)
      .switchMap(({ withAllFutureSlots }) => {
        const { editedSlot } = stateSelector(getState());
        const $update = editedSlot.id
          ? ProductScheduleSlotsRepository.update(editedSlot, { withAllFutureSlots })
          : ProductScheduleSlotsRepository.create(editedSlot, { withAllFutureSlots });
        return Observable.concat(
          Observable.of({ type: FETCH_SAVE_START }),
          Observable.of(removeAlert({ uuid: FETCH_SAVE_FAILURE })),
          $update
            .switchMap((slot) => {
              return Observable.concat(
                Observable.of({ type: FETCH_SAVE_SUCCESS, slot }),
                Observable.of({ type: FETCH_SLOTS }),
                Observable.of(closeForm())
              );
            })
            .catch((error) => {
              st.api.handleError(error, { getState, dispatch });
              // const errors = new st.form.ValidationErrors([ ["base", st.api.findResponseErrorMessage(error)] ])
              return Observable.concat(
                // Observable.of({ type: SET_VALIDATION_ERRORS, errors }),
                Observable.of({ type: FETCH_SAVE_FAILURE, error }),
                Observable.of(
                  createAlert({
                    uuid: FETCH_SAVE_FAILURE,
                    type: "error",
                    content: st.api.findResponseErrorMessage(error),
                    timeout: false,
                  })
                )
              );
            })
        );
      }),

  (actions$, { getState, dispatch }) =>
    actions$.ofType(REMOVE_SLOT).switchMap(({ slot, withAllFutureSlots }) => {
      return Observable.concat(
        Observable.of({ type: FETCH_REMOVE_SLOT_START, slot }),
        Observable.of(removeAlert({ uuid: FETCH_REMOVE_SLOT_FAILURE })),
        ProductScheduleSlotsRepository.delete(slot.id, { withAllFutureSlots })
          .switchMap(() => {
            return Observable.concat(
              Observable.of({ type: FETCH_REMOVE_SLOT_SUCCESS, slot }),
              withAllFutureSlots ? Observable.of({ type: FETCH_SLOTS }) : Observable.empty(),
              Observable.of(closeForm())
            );
          })
          .catch((error) => {
            st.api.handleError(error, { getState, dispatch });
            // const errors = new st.form.ValidationErrors([ ["base", st.api.findResponseErrorMessage(error)] ])
            return Observable.concat(
              // Observable.of({ type: SET_VALIDATION_ERRORS, errors }),
              Observable.of({ type: FETCH_REMOVE_SLOT_FAILURE, error }),
              Observable.of(
                createAlert({
                  uuid: FETCH_REMOVE_SLOT_FAILURE,
                  type: "error",
                  content: st.api.findResponseErrorMessage(error),
                  timeout: false,
                })
              )
            );
          })
      );
    }),
];

export function initializeView(product) {
  return { type: INITIALIZE_VIEW, product };
}

export function openSlotForm(slot) {
  return { type: OPEN_SLOT_FORM, slot };
}

export function closeForm() {
  return { type: CLOSE_FORM };
}

export function updateSlot(slot) {
  return { type: UPDATE_SLOT, slot };
}

export function removeSlot(slot, { withAllFutureSlots = true } = {}) {
  return { type: REMOVE_SLOT, slot, withAllFutureSlots };
}

export function validate({ submitValidate = false } = {}) {
  return { type: VALIDATE, submitValidate };
}

export function save({ withAllFutureSlots = true } = {}) {
  return { type: SAVE, withAllFutureSlots };
}

export function setDateRange(dateRange) {
  return { type: SET_DATE_RANGE, dateRange };
}

export function setIsScheduleBeingEdited(isScheduleBeingEdited) {
  return { type: SET_IS_SCHEDULE_FORM_OPENED, isScheduleBeingEdited: !!isScheduleBeingEdited };
}

export default st.duck.createDuck(initialState, reducers, epics);



// WEBPACK FOOTER //
// ./src/modules/products/EditProductView/StepAvailability/index.js